import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, euro, formatApiError } from "../lib/api";
import { C, field, btnPrimary, softShadow } from "../lib/theme";
import { toast } from "sonner";
import { CalendarDays, Users, Euro, ClipboardList, Check, X, RotateCcw, Inbox, Link2, Send, Plus } from "lucide-react";

const STATUS = {
  requested: { label: "Richiesta", bg: "#EFE7FB", fg: "#6D4AAE" },
  awaiting_payment: { label: "Da pagare", bg: "#FEEDE7", fg: "#C2410C" },
  confirmed: { label: "Confermata", bg: "#E3F1EB", fg: "#12715A" },
  declined: { label: "Rifiutata", bg: "#FDE8E8", fg: "#B42318" },
  cancelled: { label: "Annullata", bg: "#FDE8E8", fg: "#B42318" },
  refunded: { label: "Rimborsata", bg: "#E5EEFB", fg: "#1D5AC2" },
  expired: { label: "Scaduta", bg: "#EFEAE0", fg: "#7A7364" },
};

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-2xl p-6" style={softShadow}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold" style={{ color: C.MUTED }}>{label}</span>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${color}1a` }}><Icon className="w-4 h-4" style={{ color }} /></div>
      </div>
      <p className="font-display text-3xl font-extrabold" style={{ color: C.INK }}>{value}</p>
    </div>
  );
}

const EMPTY_MANUAL = { customer_name: "", customer_phone: "", customer_email: "", date: "", time: "", party_size: 2, deposit_amount: "", notes: "" };

export default function OwnerDashboard() {
  const [stats, setStats] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [filter, setFilter] = useState("all");
  const [acceptTarget, setAcceptTarget] = useState(null);
  const [acceptAmount, setAcceptAmount] = useState("");
  const [declineTarget, setDeclineTarget] = useState(null);
  const [declineReason, setDeclineReason] = useState("");
  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState(EMPTY_MANUAL);
  const [savingManual, setSavingManual] = useState(false);

  const load = () => {
    api.get("/owner/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/owner/reservations").then((r) => setReservations(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const openAccept = (r) => { setAcceptTarget(r); setAcceptAmount(String(r.suggested_deposit || "")); };
  const doAccept = async () => {
    const amt = parseFloat(acceptAmount);
    if (!amt || amt <= 0) { toast.error("Inserisci un importo valido"); return; }
    try {
      await api.post(`/owner/reservations/${acceptTarget.id}/accept`, { deposit_amount: amt, origin_url: window.location.origin });
      toast.success("Richiesta accettata: link di pagamento inviato al cliente");
      setAcceptTarget(null); setAcceptAmount(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doDecline = async () => {
    try {
      await api.post(`/owner/reservations/${declineTarget.id}/decline`, { reason: declineReason });
      toast.success("Richiesta rifiutata");
      setDeclineTarget(null); setDeclineReason(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const setStatus = async (id, status) => {
    try { await api.patch(`/owner/reservations/${id}/status`, { status }); toast.success("Stato aggiornato"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const refund = async (id) => {
    if (!window.confirm("Rimborsare la caparra e annullare la prenotazione? L'operazione non è reversibile.")) return;
    try { await api.post(`/owner/reservations/${id}/refund`); toast.success("Caparra rimborsata al cliente"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const copyPayLink = (r) => { navigator.clipboard.writeText(`${window.location.origin}/paga/${r.pay_token}`); toast.success("Link di pagamento copiato"); };

  const doManual = async () => {
    const amt = parseFloat(manual.deposit_amount);
    if (!manual.customer_name || !manual.customer_phone || !manual.date || !manual.time || !amt || amt <= 0) {
      toast.error("Compila nome, telefono, data, orario e importo caparra"); return;
    }
    setSavingManual(true);
    try {
      const payload = { ...manual, party_size: Number(manual.party_size), deposit_amount: amt };
      if (!payload.customer_email) delete payload.customer_email;
      const { data } = await api.post("/owner/reservations/manual", payload);
      navigator.clipboard.writeText(`${window.location.origin}/paga/${data.pay_token}`);
      toast.success(manual.customer_email ? "Prenotazione creata, email inviata e link copiato" : "Prenotazione creata, link copiato negli appunti");
      setManualOpen(false); setManual(EMPTY_MANUAL); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingManual(false); }
  };

  const filtered = reservations.filter((r) => filter === "all" || r.status === filter);
  const payLabel = (r) => r.payment_status === "paid" ? "Versata" : r.payment_status === "refunded" ? "Rimborsata" : r.status === "awaiting_payment" ? "In attesa" : "—";

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
      <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
        <div>
          <h1 className="font-display text-4xl font-extrabold mb-1" style={{ color: C.INK }}>Dashboard</h1>
          <p style={{ color: C.MUTED }}>Gestisci le richieste, accetta e imposta la caparra.</p>
        </div>
        <button data-testid="manual-booking-btn" onClick={() => setManualOpen(true)} className={btnPrimary}>
          <Plus className="w-4 h-4" /> Nuova prenotazione (telefono/WhatsApp)
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
        <Stat icon={ClipboardList} label="Richieste da gestire" value={stats?.pending_requests ?? "—"} color={C.CORAL} />
        <Stat icon={CalendarDays} label="Prenotazioni oggi" value={stats?.today_bookings ?? "—"} color={C.GREEN} />
        <Stat icon={Users} label="Coperti oggi" value={stats?.total_covers_today ?? "—"} color="#B4741A" />
        <Stat icon={Euro} label="Caparre incassate" value={stats ? euro(stats.deposits_collected) : "—"} color={C.GREEN} />
      </div>

      <div className="flex items-center gap-2 mb-5 flex-wrap">
        {[["all", "Tutte"], ["requested", "Richieste"], ["awaiting_payment", "Da pagare"], ["confirmed", "Confermate"], ["cancelled", "Annullate"]].map(([k, l]) => (
          <button key={k} data-testid={`filter-${k}`} onClick={() => setFilter(k)}
            className="px-4 py-2 rounded-full text-sm font-semibold transition-colors border" style={filter === k ? { background: C.CORAL, color: "#fff", borderColor: C.CORAL } : { background: "#fff", color: "#4a453f", borderColor: C.BORDER }}>{l}</button>
        ))}
      </div>

      <div className="bg-white rounded-3xl overflow-hidden" style={softShadow}>
        {filtered.length === 0 ? (
          <div className="p-16 text-center"><Inbox className="w-12 h-12 mx-auto mb-3" style={{ color: "#d8cfbb" }} /><p style={{ color: C.MUTED }}>Nessuna prenotazione in questa vista.</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="uppercase text-xs tracking-wider" style={{ background: "#FBF6EC", color: C.MUTED }}>
                <tr>
                  <th className="text-left px-6 py-4 font-bold">Cliente</th>
                  <th className="text-left px-6 py-4 font-bold">Data / Ora</th>
                  <th className="text-left px-6 py-4 font-bold">Persone</th>
                  <th className="text-left px-6 py-4 font-bold">Caparra</th>
                  <th className="text-left px-6 py-4 font-bold">Stato</th>
                  <th className="text-right px-6 py-4 font-bold">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: "#f2ecdd" }}>
                {filtered.map((r) => {
                  const s = STATUS[r.status] || STATUS.requested;
                  return (
                    <motion.tr key={r.id} data-testid={`owner-row-${r.id}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="transition-colors hover:bg-[#FBF6EC]/60" style={{ borderColor: "#f2ecdd" }}>
                      <td className="px-6 py-4">
                        <p className="font-bold" style={{ color: C.INK }}>{r.customer_name}</p>
                        <p className="text-xs" style={{ color: C.MUTED }}>{r.customer_phone}</p>
                        {r.notes ? <p className="text-xs italic mt-1 max-w-[200px] truncate" style={{ color: C.MUTED }}>“{r.notes}”</p> : null}
                      </td>
                      <td className="px-6 py-4" style={{ color: "#4a453f" }}>{r.date}<br /><span className="font-bold" style={{ color: C.CORAL }}>{r.time}</span></td>
                      <td className="px-6 py-4 font-semibold" style={{ color: "#4a453f" }}>{r.party_size}</td>
                      <td className="px-6 py-4">
                        <span className="font-bold" style={{ color: C.GREEN }}>{r.deposit_amount > 0 ? euro(r.deposit_amount) : (r.suggested_deposit ? `~${euro(r.suggested_deposit)}` : "—")}</span>
                        <br /><span className="text-xs" style={{ color: C.MUTED }}>{payLabel(r)}</span>
                      </td>
                      <td className="px-6 py-4"><span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: s.bg, color: s.fg }}>{s.label}</span></td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          {r.status === "requested" && (
                            <>
                              <button data-testid={`accept-${r.id}`} onClick={() => openAccept(r)} title="Accetta e imposta caparra"
                                className="h-8 px-3 rounded-lg flex items-center gap-1.5 text-xs font-bold transition-colors" style={{ background: "#E3F1EB", color: C.GREEN }}><Check className="w-4 h-4" /> Accetta</button>
                              <button data-testid={`decline-${r.id}`} onClick={() => { setDeclineTarget(r); setDeclineReason(""); }} title="Rifiuta"
                                className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors" style={{ background: "#FDE8E8", color: "#B42318" }}><X className="w-4 h-4" /></button>
                            </>
                          )}
                          {r.status === "awaiting_payment" && (
                            <>
                              <button data-testid={`paylink-${r.id}`} onClick={() => copyPayLink(r)} title="Copia link di pagamento"
                                className="h-8 px-3 rounded-lg flex items-center gap-1.5 text-xs font-bold transition-colors" style={{ background: "#FEEDE7", color: C.CORAL }}><Link2 className="w-4 h-4" /> Link</button>
                              <button data-testid={`cancel-${r.id}`} onClick={() => setStatus(r.id, "cancelled")} title="Annulla"
                                className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors" style={{ background: "#FDE8E8", color: "#B42318" }}><X className="w-4 h-4" /></button>
                            </>
                          )}
                          {r.status === "confirmed" && r.payment_status === "paid" && (
                            <button data-testid={`refund-${r.id}`} onClick={() => refund(r.id)} title="Rimborsa caparra e annulla"
                              className="h-8 px-3 rounded-lg flex items-center gap-1.5 text-xs font-bold transition-colors" style={{ background: "#E5EEFB", color: "#1D5AC2" }}><RotateCcw className="w-4 h-4" /> Rimborsa</button>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Accept modal */}
      {acceptTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-5" onClick={() => setAcceptTarget(null)}>
          <div className="bg-white rounded-3xl p-8 max-w-sm w-full" style={softShadow} onClick={(e) => e.stopPropagation()} data-testid="accept-modal">
            <h3 className="font-display text-2xl font-extrabold mb-1" style={{ color: C.INK }}>Accetta richiesta</h3>
            <p className="text-sm mb-5" style={{ color: C.MUTED }}>Imposta la caparra per {acceptTarget.customer_name} · {acceptTarget.party_size} persone. Il cliente riceverà il link di pagamento via email.</p>
            <label className="block text-sm font-semibold mb-2" style={{ color: "#4a453f" }}>Importo caparra (€)</label>
            <input data-testid="accept-amount" type="number" min="1" step="0.5" value={acceptAmount} onChange={(e) => setAcceptAmount(e.target.value)} className={`${field} mb-5`} autoFocus />
            <div className="flex gap-3">
              <button onClick={() => setAcceptTarget(null)} className="flex-1 py-3 rounded-full border-2 font-bold transition-colors hover:bg-black/5" style={{ borderColor: "#e2d8c3", color: "#4a453f" }}>Annulla</button>
              <button data-testid="accept-confirm" onClick={doAccept} className={`${btnPrimary} flex-1`}><Send className="w-4 h-4" /> Invia link</button>
            </div>
          </div>
        </div>
      )}

      {/* Decline modal */}
      {declineTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-5" onClick={() => setDeclineTarget(null)}>
          <div className="bg-white rounded-3xl p-8 max-w-sm w-full" style={softShadow} onClick={(e) => e.stopPropagation()} data-testid="decline-modal">
            <h3 className="font-display text-2xl font-extrabold mb-1" style={{ color: C.INK }}>Rifiuta richiesta</h3>
            <p className="text-sm mb-5" style={{ color: C.MUTED }}>Puoi aggiungere un messaggio per {declineTarget.customer_name}: verrà incluso nell'email di rifiuto.</p>
            <label className="block text-sm font-semibold mb-2" style={{ color: "#4a453f" }}>Motivo (facoltativo)</label>
            <textarea data-testid="decline-reason" rows={3} value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} className={`${field} resize-none mb-5`} placeholder="Es. Siamo al completo per quella data, propongo un altro orario…" />
            <div className="flex gap-3">
              <button onClick={() => setDeclineTarget(null)} className="flex-1 py-3 rounded-full border-2 font-bold transition-colors hover:bg-black/5" style={{ borderColor: "#e2d8c3", color: "#4a453f" }}>Annulla</button>
              <button data-testid="decline-confirm" onClick={doDecline} className="flex-1 py-3 rounded-full font-bold text-white transition-transform hover:-translate-y-0.5 flex items-center justify-center gap-2" style={{ background: "#B42318" }}><X className="w-4 h-4" /> Rifiuta e invia</button>
            </div>
          </div>
        </div>
      )}

      {/* Manual booking modal */}
      {manualOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-5 py-8 overflow-y-auto" onClick={() => setManualOpen(false)}>
          <div className="bg-white rounded-3xl p-8 max-w-lg w-full my-auto" style={softShadow} onClick={(e) => e.stopPropagation()} data-testid="manual-modal">
            <h3 className="font-display text-2xl font-extrabold mb-1" style={{ color: C.INK }}>Nuova prenotazione</h3>
            <p className="text-sm mb-5" style={{ color: C.MUTED }}>Per prenotazioni prese al telefono o via WhatsApp. Genera un link di pagamento da inviare al cliente. Se inserisci l'email, glielo mandiamo noi.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="block text-sm font-semibold mb-1.5" style={{ color: "#4a453f" }}>Nome cliente *</label><input data-testid="manual-name" className={field} value={manual.customer_name} onChange={(e) => setManual({ ...manual, customer_name: e.target.value })} /></div>
              <div><label className="block text-sm font-semibold mb-1.5" style={{ color: "#4a453f" }}>Telefono *</label><input data-testid="manual-phone" className={field} value={manual.customer_phone} onChange={(e) => setManual({ ...manual, customer_phone: e.target.value })} /></div>
              <div className="sm:col-span-2"><label className="block text-sm font-semibold mb-1.5" style={{ color: "#4a453f" }}>Email (facoltativa)</label><input data-testid="manual-email" type="email" className={field} value={manual.customer_email} onChange={(e) => setManual({ ...manual, customer_email: e.target.value })} placeholder="Se presente, inviamo il link via email" /></div>
              <div><label className="block text-sm font-semibold mb-1.5" style={{ color: "#4a453f" }}>Data *</label><input data-testid="manual-date" type="date" className={field} value={manual.date} onChange={(e) => setManual({ ...manual, date: e.target.value })} /></div>
              <div><label className="block text-sm font-semibold mb-1.5" style={{ color: "#4a453f" }}>Orario *</label><input data-testid="manual-time" type="time" className={field} value={manual.time} onChange={(e) => setManual({ ...manual, time: e.target.value })} /></div>
              <div><label className="block text-sm font-semibold mb-1.5" style={{ color: "#4a453f" }}>Persone *</label><input data-testid="manual-party" type="number" min="1" className={field} value={manual.party_size} onChange={(e) => setManual({ ...manual, party_size: e.target.value })} /></div>
              <div><label className="block text-sm font-semibold mb-1.5" style={{ color: "#4a453f" }}>Caparra (€) *</label><input data-testid="manual-deposit" type="number" min="1" step="0.5" className={field} value={manual.deposit_amount} onChange={(e) => setManual({ ...manual, deposit_amount: e.target.value })} /></div>
              <div className="sm:col-span-2"><label className="block text-sm font-semibold mb-1.5" style={{ color: "#4a453f" }}>Note</label><input data-testid="manual-notes" className={field} value={manual.notes} onChange={(e) => setManual({ ...manual, notes: e.target.value })} /></div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setManualOpen(false)} className="flex-1 py-3 rounded-full border-2 font-bold transition-colors hover:bg-black/5" style={{ borderColor: "#e2d8c3", color: "#4a453f" }}>Annulla</button>
              <button data-testid="manual-confirm" onClick={doManual} disabled={savingManual} className={`${btnPrimary} flex-1`}><Link2 className="w-4 h-4" /> {savingManual ? "Creazione…" : "Crea e copia link"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
